// Subscription plan tiers
export enum SubscriptionTier {
  FREE = 'free',
  GROWTH = 'growth',
  ENTERPRISE = 'enterprise',
  ENTERPRISE_PRO = 'enterprise_pro'
}

// Subscription plan interface
export interface SubscriptionPlan {
  _id: string;
  name: string;
  code: string;
  description: string;
  price: number;
  currency: string;
  passes: number;
  validity: number; // in days
  queuePriority: number; // 1 is highest, 5 is lowest
  features: string[];
  isActive: boolean;
  rolloverEnabled: boolean;
  whatsappEnabled: boolean;
  order: number; // For display order
  createdAt: string;
  updatedAt: string;
  isUnlimited?: boolean; // New field to identify unlimited passes plans
}

// User subscription interface
export interface UserSubscription {
  _id: string;
  userId: string;
  planId: string;
  planCode: string;
  planName: string;
  purchaseDate: string;
  expiryDate: string;
  isActive: boolean;
  isExpired: boolean;
  remainingPasses: number;
  totalPasses: number;
  amount: number;
  currency: string;
  paymentId?: string;
  orderId?: string;
  receiptNumber?: string;
  invoiceUrl?: string;
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  razorpaySignature?: string;
  gstDetails?: GSTDetails;
  renewalAttempted?: boolean;
  renewalSuccessful?: boolean;
  meta?: any;
  createdAt: string;
  updatedAt: string;
  isUnlimited?: boolean; // New field to identify unlimited passes subscriptions
}

// Payment interface
export interface Payment {
  _id: string;
  userId: string;
  subscriptionId?: string;
  planId: string;
  planCode: string;
  amount: number;
  currency: string;
  status: 'created' | 'pending' | 'completed' | 'failed' | 'refunded';
  paymentMethod?: string;
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  razorpaySignature?: string;
  receiptNumber?: string;
  invoiceUrl?: string;
  refundId?: string;
  refundAmount?: number;
  refundReason?: string;
  gstDetails?: GSTDetails;
  meta?: any;
  createdAt: string;
  updatedAt: string;
}

// GST details interface
export interface GSTDetails {
  gstNumber: string;
  businessName: string;
  businessAddress: string;
}

// Razorpay order interface
export interface RazorpayOrder {
  id: string;
  amount: number;
  currency: string;
  receipt: string;
  status: string;
  created_at: number;
}

// API response interfaces
export interface PlansResponse {
  success: boolean;
  plans: SubscriptionPlan[];
}

export interface SubscriptionsResponse {
  success: boolean;
  subscriptions: UserSubscription[];
  activeSubscriptionId?: string;
}

export interface ActiveSubscriptionResponse {
  success: boolean;
  subscription: UserSubscription;
}

export interface PaymentsResponse {
  success: boolean;
  payments: Payment[];
}

export interface CreateOrderResponse {
  success: boolean;
  orderId: string;
  amount: number;
  currency: string;
  plan: {
    id: string;
    code: string;
    name: string;
    price: number;
    passes: number;
    validity: number;
    features: string[];
    isUnlimited?: boolean;
  };
  message?: string;
  paymentUrl?: string; // Add this property for direct payment links
}

export interface VerifyPaymentResponse {
  success: boolean;
  message?: string;
  subscription?: UserSubscription;
}

export interface InvoiceResponse {
  success: boolean;
  invoiceUrl: string;
}

// Socket event types
export interface SubscriptionUpdateEvent {
  subscriptionId: string;
  planCode: string;
  planName: string;
  remainingPasses: number;
  totalPasses: number;
  isUnlimited?: boolean;
}

export interface MeetingPassesUpdateEvent {
  remaining: number;
  total: number;
  isUnlimited?: boolean;
}
